<template>
  <div id="session-history" class="py-10">
    <div class="mb-6 mx-10">
      <BlTextTitle4 text="Session history" />
      <BlTextBody2 :text="description" />
      <div v-if="therapistName" class="flex items-center gap-2 mt-2">
        <BlIcon icon="account-multiple" class="w-6 h-6" />
        <div
          class="h-8 px-2 flex items-center gap-1 rounded text-success bg-success-100"
        >
          <BlTextBody2 :text="therapistName" />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 items-center gap-4 mb-6 px-10">
      <BlInputFilterAdvance
        :activeLabel="activeFilterDate"
        :input="sessionFilterDateInput"
        @changed="onChangeFilter"
      />
      <BlInputSelect
        :input="sessionFilterClientInput"
        @changed="onChangeFilter"
      />
    </div>

    <div class="flex items-center gap-4 px-10 mb-6">
      <BlTextBody2 text="Group by" class="flex-shrink-0" />
      <button
        v-for="opt in groupOptions"
        :key="opt.value"
        class="w-full py-1 rounded border text-sm outline-none hover:bg-hover-state"
        :class="
          groupBy === opt.value
            ? 'border-light-purple text-light-purple'
            : 'border-grey-200'
        "
        @click="onChangeGroup(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>

    <div v-if="pageLoading" class="h-128 grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else>
      <div class="mb-6 mx-10">
        <div class="text-xs text-grey-400 mb-2">Number of session</div>
        <BarChart
          :key="'bar-chart'"
          class="w-full"
          :chart-data="collection"
          :options="chartoptions"
        />
        <div class="text-xs text-grey-400 mb-2 pl-6 text-center">
          Date of session
        </div>
      </div>

      <div class="w-full h-px bg-grey-200 mb-5"></div>

      <div class="mb-6 mx-10">
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <BlTextBody2 text="Showing" />
            <BlBubbleCount
              :count="
                sessionHistories.reduce(
                  (acc, obj) => acc + obj.sessions.length,
                  0
                )
              "
            />
            <BlTextBody2 text="session" />
          </div>
          <div>
            <BlInputSelect
              :input="sessionHistorySortInput"
              @changed="inputHandler"
            />
          </div>
        </div>
        <table id="blum-table">
          <thead>
            <tr>
              <td class="capitalize">
                <BlTextSubtitle2 :text="groupBy === 'day' ? 'Date' : groupBy" />
              </td>
              <td v-if="groupBy !== 'day'"><BlTextSubtitle2 text="Date" /></td>
              <td class="flex-shrink-0"><BlTextSubtitle2 text="Client" /></td>
              <td><BlTextSubtitle2 text="Duration" /></td>
              <td><BlTextSubtitle2 text="Target" /></td>
            </tr>
          </thead>
          <tbody v-if="!sessionTable.length">
            <tr>
              <td
                :colspan="groupBy === 'day' ? 4 : 5"
                class="text-center text-sm text-grey-400"
              >
                Session not found
              </td>
            </tr>
          </tbody>
          <tbody
            v-for="(sessionBody, bodyIndex) in sessionTable"
            :key="bodyIndex"
          >
            <tr v-for="(session, index) in sessionBody.sessions" :key="index">
              <td
                v-if="index === 0"
                class="bg-grey-100 text-grey-400 border-r py-2"
                :rowspan="sessionBody.number_of_session"
              >
                <span class="flex items-center gap-2 mb-1">
                  {{
                    sessionBody.date
                      | moment(
                        groupBy === "day"
                          ? "dddd"
                          : groupBy === "year"
                          ? "YYYY"
                          : "MMMM"
                      )
                  }}
                  <BlBubbleCount :count="sessionBody.number_of_session || 0" />
                </span>
                <span>{{ dateColumn(sessionBody.date) }}</span>
              </td>
              <td v-if="groupBy !== 'day'" class="text-grey">
                {{ session.end_time | moment("DD/MM/YYYY") }}
              </td>
              <td class="text-grey">
                <div
                  class="truncate"
                  :class="groupBy === 'day' ? 'w-70' : 'w-22'"
                  :title="session.client ? session.client.name : '-'"
                >
                  {{ session.client ? session.client.name : "-" }}
                </div>
              </td>
              <td class="text-grey-400">
                {{ session.current_recording_time[1] }}
              </td>
              <td class="text-grey-400 text-center">
                {{ session.number_of_measurements || 0 }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";
import BlInputFilterAdvance from "../BlInputFilterAdvance.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BarChart from "../charts/BarChart.js";

export default {
  name: "TabSession",
  components: {
    BlTextTitle4,
    BlTextBody2,
    BlBubbleCount,
    BlInputSelect,
    BlTextSubtitle2,
    BlIcon,
    BlInputFilterAdvance,
    BarChart
  },
  props: {
    description: { type: String },
    therapistName: { type: String },
    basePath: { type: String },
    userId: { type: Number }
  },
  data() {
    return {
      pageLoading: true,
      sessionFilterDateInput: {
        model: { date: null },
        tabs: [{ name: "Date", kind: "date" }]
      },
      sessionFilterClientInput: {
        model: { name: "All", value: "*" },
        prefix: "Client",
        combine: true,
        search: true,
        options: []
      },
      groupBy: "day",
      groupOptions: [
        { value: "day", label: "Day" },
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
        { value: "year", label: "Year" }
      ],
      sessionHistorySortInput: {
        model: "Most recent",
        prefix: "Sort by",
        options: ["Most recent", "Oldest"]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "sessionHistories"]),
    chartoptions() {
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            title: a => a[0].label.replace(",", "/")
          }
        },
        scales: {
          yAxes: [
            {
              ticks: { suggestedMin: 0 },
              gridLines: { display: true }
            }
          ],
          xAxes: [
            {
              ticks: {
                callback: label => {
                  if (/\s/.test(label)) return label.split("  ");
                  else return label;
                }
              },
              gridLines: { display: false },
              barPercentage: 0.4
            }
          ]
        }
      };
    },
    collection() {
      const data = this.sessionHistories.map(s => s.number_of_session);
      const label = "Number of session";
      const dateFormats = {
        day: "DD/MM  YYYY",
        month: "MMMM  YYYY",
        year: "YYYY"
      };
      function weekOfMonth(m) {
        return (
          m.week() -
          moment(m)
            .startOf("month")
            .week() +
          1
        );
      }
      const datacollection = {
        labels: this.sessionHistories.map(s => {
          if (this.groupBy !== "week") {
            return moment(s.date).format(dateFormats[this.groupBy]);
          }
          return `Week #${weekOfMonth(moment(s.date))} ${moment(s.date).format(
            "MMMM  YYYY"
          )}`;
        }),
        datasets: [
          {
            label: label,
            backgroundColor: "#B3A7D1",
            data: data
          }
        ]
      };
      return datacollection;
    },
    activeFilterDate() {
      const filter = this.sessionFilterDateInput.model.date;
      let start = "";
      let end = moment(new Date());
      if (filter) {
        if (typeof filter === "string") {
          const range = filter.replace("last_", "");
          start = moment(end).subtract(1, range);
        } else {
          if (filter.start_date) start = moment(filter.start_date);
          if (filter.end_date) end = moment(filter.end_date);
        }
      }
      return start
        ? `Date ${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`
        : `Filter by date`;
    },
    sessionTable() {
      const data = [...this.sessionHistories];
      if (data.length) {
        if (this.sessionHistorySortInput.model === "Oldest") {
          data.sort((a, b) => new Date(a.date) - new Date(b.date));
        } else {
          data.sort((a, b) => new Date(b.date) - new Date(a.date));
        }
      }
      return data;
    },
    getParams() {
      let params = "";
      const filterDate = this.sessionFilterDateInput.model.date;
      const filterClient = this.sessionFilterClientInput.model.value;
      if (filterDate) {
        if (typeof filterDate === "string") {
          params += `&filter_by=${filterDate}`;
        } else {
          if (filterDate.start_date) {
            params += `&start_date=${filterDate.start_date}`;
          }
          if (filterDate.end_date) {
            params += `&end_date=${filterDate.end_date}`;
          }
        }
      }
      if (filterClient !== "*") {
        params += `&client_id=${filterClient}`;
      }
      params += `&group_by=${this.groupBy}`;
      return params;
    }
  },
  watch: {
    sessionHistories(val) {
      const data = [...val];
      const opts = [{ name: "All", value: "*" }];
      const idSet = new Set();

      for (const row of data) {
        for (const session of row.sessions) {
          if (session.client && !idSet.has(session.client.id)) {
            const opt = { name: session.client.name, value: session.client.id };
            opts.push(opt);
            idSet.add(session.client.id);
          }
        }
      }
      const combined = this.getUniqueArrayById([
        ...this.sessionFilterClientInput.options,
        ...opts
      ]);
      this.sessionFilterClientInput.options = combined;
    }
  },
  mounted() {
    this.pageLoading = true;
    const query = this.$route.query;

    if (query.filter_by) {
      this.sessionFilterDateInput.model.date = query.filter_by;
    }

    if (query.start_date || query.end_date) {
      this.sessionFilterDateInput.model.date = {
        start_date: query.start_date || "",
        end_date: query.end_date || ""
      };
    }

    if (query.client_id) {
      const client = this.sessionFilterClientInput.options.find(
        c => Number(c.value) === Number(query.client_id)
      );
      this.sessionFilterClientInput.model = {
        name: client ? client.name : "All",
        value: Number(query.client_id)
      };
    }

    if (query.group_by) this.groupBy = query.group_by;

    this.fetchSessions();
  },
  methods: {
    ...mapActions(["getUserSessionHistories"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async fetchSessions() {
      const params = {
        id: this.userId,
        params: `?status=completed&sort=oldest&page=1&per_page=9999${this.getParams}`
      };
      const { success } = await this.getUserSessionHistories(params);
      this.pageLoading = false;
      if (!success) return;

      // sync url path for page
      const incomingPath = this.basePath + this.getParams;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.groupBy = "day";
      this.pageLoading = true;
      this.fetchSessions();
    },
    onChangeGroup(val) {
      this.groupBy = val;
      this.pageLoading = true;
      this.fetchSessions();
    },
    dateColumn(date) {
      if (this.groupBy === "day") return moment(date).format("DD/MM/YYYY");
      else {
        const start = moment(date).format("DD/MM/YYYY");
        const end = moment(date)
          .add(1, this.groupBy)
          .subtract(1, "day")
          .format("DD/MM/YYYY");
        return `${start}-${end}`;
      }
    },
    getUniqueArrayById(originalArray) {
      const uniqueArray = [];
      const values = [];
      for (var i = 0; i < originalArray.length; i++) {
        var element = originalArray[i];
        if (!values.includes(element.value)) {
          values.push(element.value);
          uniqueArray.push(element);
        }
      }
      return uniqueArray;
    },
    generateKeyChart() {
      let result = "";
      const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      const charactersLength = characters.length;
      for (let i = 0; i < 9; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
      }
      return result;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/profile/TabSession.vue